import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { isValid, parse, startOfDay } from 'date-fns';

const DAY_PARAM_FORMAT = 'MM-dd-yyyy';

/**
 * The schedule period the settings page was opened for.
 *
 * The schedule writes the selected day to `?day=` and the Settings link forwards the whole query
 * string both as a search param (survives a reload) and as navigation state (what the "Back to
 * Schedule" link is rebuilt from), so either source answers the question.
 *
 * Opening the page with no context at all - a bookmark, or the browser's history - falls back to
 * today's period, which is intended: the other family is reached by navigating the schedule to a
 * period on the other side of the cutover and re-opening Settings.
 */
export const useCSSettingsSelectedDay = (): Date => {
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const day = searchParams.get('day') ?? (state as { day?: string } | null)?.day;

  return useMemo(() => {
    if (!day) return startOfDay(new Date());

    const parsed = parse(day, DAY_PARAM_FORMAT, new Date());
    return startOfDay(isValid(parsed) ? parsed : new Date());
  }, [day]);
};
